import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Trash, Copy, Eye, EyeOff, Plus } from "lucide-react";
import { useState, useEffect, useCallback } from "react";

interface WalletDetails {
	type: "solana" | "ethereum";
	name: string;
	publicKey: string;
	privateKey: string;
}

interface StoredData {
	mnemonic: string;
	wallets: WalletDetails[];
}

const Dashboard = () => {
	const [mnemonic, setMnemonic] = useState<string>("");
	const [walletDetails, setWalletDetails] = useState<WalletDetails[]>([]);
	const [showPrivateKey, setShowPrivateKey] = useState<Record<number, boolean>>({});
	const [showRecoveryPhrase, setShowRecoveryPhrase] = useState(false);
	const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

	useEffect(() => {
		const storedData = localStorage.getItem("walletData");
		if (!storedData) {
			window.location.href = "/";
			return;
		}

		try {
			const data: StoredData = JSON.parse(storedData);
			if (data.mnemonic && Array.isArray(data.wallets) && data.wallets.length > 0) {
				setMnemonic(data.mnemonic);
				setWalletDetails(data.wallets);
			}
		} catch (e) {
			console.error("Failed to parse stored data:", e);
			localStorage.removeItem("walletData");
			setMnemonic("");
			setWalletDetails([]);
		}
	}, []);

	const saveData = useCallback((currentMnemonic: string, wallets: WalletDetails[]) => {
		const data: StoredData = { mnemonic: currentMnemonic, wallets };
		localStorage.setItem("walletData", JSON.stringify(data));
	}, []);

	const handleDeleteWallet = (index: number) => {
		if (confirm("Are you sure you want to delete this wallet? This action cannot be undone.")) {
			const updatedWallets = walletDetails.filter((_, i) => i !== index);
			setWalletDetails(updatedWallets);
			saveData(mnemonic, updatedWallets);

			if (updatedWallets.length === 0) {
				setMnemonic("");
				localStorage.removeItem("walletData");
				window.location.href = "/";
			}
		}
	};

	const handleClearAllWallets = () => {
		if (confirm("Are you sure you want to delete ALL wallets? This action cannot be undone.")) {
			setWalletDetails([]);
			setMnemonic("");
			localStorage.removeItem("walletData");
			window.location.href = "/";
		}
	};

	const copyToClipboard = async (text: string, index: number) => {
		try {
			await navigator.clipboard.writeText(text);
			setCopiedIndex(index);
			setTimeout(() => setCopiedIndex(null), 2000);
		} catch (err) {
			console.error("Failed to copy:", err);
		}
	};

	const togglePrivateKey = (index: number) => {
		setShowPrivateKey((prev) => ({ ...prev, [index]: !prev[index] }));
	};

	const truncateKey = (key: string, chars: number = 8) => {
		if (key.length <= chars * 2) return key;
		return `${key.slice(0, chars)}...${key.slice(-chars)}`;
	};

	return (
		<section className="flex-1 px-6 py-8 overflow-auto">
			<div className="w-full max-w-4xl mx-auto">
				<div className="flex items-center justify-between mb-8">
					<div>
						<h1 className="text-3xl font-bold mb-2 text-white">Your Wallets</h1>
						<p className="text-neutral-500">
							Manage your {walletDetails.length} wallet{walletDetails.length !== 1 ? "s" : ""}
						</p>
					</div>
					<div className="flex gap-3">
						<Button
							onClick={() => {
								window.location.href = "/";
							}}
							className="h-11 px-5 bg-white hover:bg-neutral-200 text-black font-semibold rounded-xl"
						>
							<Plus className="h-4 w-4 mr-2" />
							Add Wallet
						</Button>
						<Button
							onClick={handleClearAllWallets}
							variant="outline"
							className="h-11 px-5 bg-transparent border-red-900/50 hover:bg-red-950/50 text-red-500 hover:text-red-400 font-medium rounded-xl"
						>
							<Trash className="h-4 w-4 mr-2" />
							Clear All
						</Button>
					</div>
				</div>

				{mnemonic && (
					<Card className="bg-neutral-950 border-neutral-800 mb-6">
						<CardHeader className="pb-2">
							<div className="flex items-center justify-between">
								<div>
									<CardTitle className="text-white text-lg">Recovery Phrase</CardTitle>
									<CardDescription className="text-neutral-500">
										This phrase is used for all your wallets. Keep it safe and never share it.
									</CardDescription>
								</div>
								<div className="flex gap-2">
									<Button
										onClick={() => setShowRecoveryPhrase(!showRecoveryPhrase)}
										variant="ghost"
										size="sm"
										className="h-8 px-3 text-neutral-500 hover:text-blue-900"
									>
										{showRecoveryPhrase ? (
											<>
												<EyeOff className="h-4 w-4 mr-1" /> Hide
											</>
										) : (
											<>
												<Eye className="h-4 w-4 mr-1" /> Show
											</>
										)}
									</Button>
									<Button
										onClick={() => copyToClipboard(mnemonic, -1)}
										variant="ghost"
										size="sm"
										className="h-8 px-3 text-neutral-500 hover:text-blue-900"
									>
										<Copy className="h-4 w-4 mr-1" />
										{copiedIndex === -1 ? "Copied!" : "Copy"}
									</Button>
								</div>
							</div>
						</CardHeader>
						{showRecoveryPhrase && (
							<CardContent>
								<div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
									{mnemonic.split(" ").map((word, wordIndex) => (
										<div
											key={wordIndex}
											className="flex items-center gap-2 bg-black border border-neutral-800 px-2 py-1.5 rounded text-sm"
										>
											<span className="text-neutral-600 text-xs w-4">{wordIndex + 1}.</span>
											<span className="text-white font-mono">{word}</span>
										</div>
									))}
								</div>
							</CardContent>
						)}
					</Card>
				)}

				<div className="grid gap-4">
					{walletDetails.map((wallet, index) => (
						<Card
							key={index}
							className="bg-neutral-950 border-neutral-800 hover:border-neutral-700 transition-colors"
						>
							<CardHeader className="pb-2">
								<div className="flex items-center justify-between">
									<div className="flex items-center gap-3">
										<div
											className={`h-10 w-10 rounded-full bg-neutral-900 border flex items-center justify-center ${
												wallet.type === "solana" ? "border-emerald-500/30" : "border-blue-500/30"
											}`}
										>
											<span
												className={`text-sm font-bold ${
													wallet.type === "solana" ? "text-emerald-500" : "text-blue-500"
												}`}
											>
												{wallet.type === "solana" ? "S" : "Ξ"}
											</span>
										</div>
										<div>
											<CardTitle className="text-white text-lg">{wallet.name}</CardTitle>
											<CardDescription className="capitalize text-neutral-600">
												{wallet.type}
											</CardDescription>
										</div>
									</div>
									<Button
										onClick={() => handleDeleteWallet(index)}
										variant="ghost"
										size="icon"
										className="text-neutral-600 hover:text-red-500 hover:bg-red-950/30"
									>
										<Trash className="h-4 w-4" />
									</Button>
								</div>
							</CardHeader>
							<CardContent className="space-y-4">
								<div className="p-3 bg-neutral-900 border border-neutral-800 rounded-lg">
									<div className="flex items-center justify-between mb-1">
										<span className="text-xs text-neutral-500 uppercase tracking-wide">
											Public Key
										</span>
										<Button
											onClick={() => copyToClipboard(wallet.publicKey, index)}
											variant="ghost"
											size="sm"
											className="h-7 px-2 text-neutral-500 hover:text-blue-900"
										>
											<Copy className="h-3 w-3 mr-1" />
											{copiedIndex === index ? "Copied!" : "Copy"}
										</Button>
									</div>
									<p className="font-mono text-sm text-white break-all">
										{truncateKey(wallet.publicKey)}
									</p>
								</div>

								<div className="p-3 bg-neutral-900 border border-neutral-800 rounded-lg">
									<div className="flex items-center justify-between mb-1">
										<span className="text-xs text-neutral-500 uppercase tracking-wide">
											Private Key
										</span>
										<div className="flex gap-1">
											<Button
												onClick={() => togglePrivateKey(index)}
												variant="ghost"
												size="sm"
												className="h-7 px-2 text-neutral-500 hover:text-blue-900"
											>
												{showPrivateKey[index] ? (
													<>
														<EyeOff className="h-3 w-3 mr-1" /> Hide
													</>
												) : (
													<>
														<Eye className="h-3 w-3 mr-1" /> Show
													</>
												)}
											</Button>
											<Button
												onClick={() => copyToClipboard(wallet.privateKey, index + 1000)}
												variant="ghost"
												size="sm"
												className="h-7 px-2 text-neutral-500 hover:text-blue-900"
											>
												<Copy className="h-3 w-3 mr-1" />
												{copiedIndex === index + 1000 ? "Copied!" : "Copy"}
											</Button>
										</div>
									</div>
									<p className="font-mono text-sm text-white break-all">
										{showPrivateKey[index] ? truncateKey(wallet.privateKey) : "•".repeat(16)}
									</p>
								</div>
							</CardContent>
						</Card>
					))}
				</div>
			</div>
		</section>
	);
};

export default Dashboard;
